import React, { useState, useEffect } from 'react';
import {
  FileText, Calendar, Mail, Radio, UserPlus, Send, BarChart3, ArrowRight,
  ClipboardCheck, CreditCard, Activity, Eye, AlertTriangle, CheckCircle2, Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (pence, currency = 'gbp') => {
  const sym = { gbp: '£', usd: '$', eur: '€' }[currency?.toLowerCase()] ?? '';
  return `${sym}${(pence / 100).toFixed(2)}`;
};

const StatCard = ({ label, value, sub, Icon, color, link }) => {
  const Wrapper = link ? Link : 'div';
  return (
    <Wrapper to={link || '#'} className={`bg-navy border border-gold/[0.06] rounded-xl p-5 ${link ? 'hover:border-gold/15 transition-all group' : ''}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={16} className={color} />
        {link && <ArrowRight size={12} className="text-cream/10 group-hover:text-gold/40 transition-colors" />}
      </div>
      <p className="text-cream text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-cream/25 text-xs mt-1">{label}</p>
      {sub && <p className="text-cream/15 text-[10px] mt-0.5">{sub}</p>}
    </Wrapper>
  );
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [cfqStats, setCfqStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [payments, setPayments] = useState(null);
  const [nurtureStats, setNurtureStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [cmsRes, cfqRes, anlRes, payRes, nurRes] = await Promise.all([
          fetch(`${API_URL}/api/cms/dashboard/stats`, { headers: h }),
          fetch(`${API_URL}/api/cfq/admin/stats`, { headers: h }),
          fetch(`${API_URL}/api/cms/analytics`, { headers: h }),
          fetch(`${API_URL}/api/cms/payments/summary?period=today`, { headers: h }),
          fetch(`${API_URL}/api/cfq/nurture/admin/stats`, { headers: h }),
        ]);
        if (cmsRes.ok) setStats(await cmsRes.json());
        if (cfqRes.ok) setCfqStats(await cfqRes.json());
        if (anlRes.ok) setAnalytics(await anlRes.json());
        if (payRes.ok) setPayments(await payRes.json());
        if (nurRes.ok) setNurtureStats(await nurRes.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayVisitors = analytics?.site_visits?.today_clean ?? analytics?.site_visits?.today ?? 0;
  const todayRevenue = payments?.gross_revenue ?? 0;
  const todayPayments = payments?.count_succeeded ?? 0;
  const nurtureActive = nurtureStats?.active_in_sequence ?? 0;

  const todayCards = [
    { label: 'Site Visitors', value: todayVisitors, Icon: Eye, color: 'text-teal-400', link: '/admin/analytics' },
    { label: "Today's Revenue", value: fmt(todayRevenue), Icon: CreditCard, color: 'text-emerald-400', link: '/admin/payments' },
    { label: 'Payments Today', value: todayPayments, Icon: CheckCircle2, color: 'text-green-400', link: '/admin/payments' },
    { label: 'Active Nurture', value: nurtureActive, Icon: Activity, color: 'text-amber-400', link: '/admin/nurture' },
    { label: "Today's CFQ", value: cfqStats?.today_completions ?? 0, Icon: ClipboardCheck, color: 'text-purple-400', link: '/admin/cfq' },
    { label: 'Live Sessions', value: cfqStats?.live_sessions ?? 0, Icon: Radio, color: 'text-red-400', link: '/admin/sessions' },
    { label: 'New Subscribers (7d)', value: cfqStats?.new_subscribers ?? 0, Icon: Mail, color: 'text-sky-400', link: '/admin/mailing' },
    { label: 'Article Views', value: analytics?.site_visits?.article_views ?? 0, Icon: BarChart3, color: 'text-gold', link: '/admin/analytics' },
  ];

  // Attention-required items
  const alerts = [];
  if (payments?.count_failed > 0) alerts.push({ msg: `${payments.count_failed} failed payment${payments.count_failed > 1 ? 's' : ''} today`, link: '/admin/payments' });
  if (nurtureStats?.sequence_errors > 0) alerts.push({ msg: `${nurtureStats.sequence_errors} nurture send failure${nurtureStats.sequence_errors > 1 ? 's' : ''}`, link: '/admin/nurture' });
  if (analytics?.site_visits?.bot_filtered > 100) alerts.push({ msg: `High bot traffic: ${analytics.site_visits.bot_filtered} filtered this period`, link: '/admin/analytics' });

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-cream font-semibold">Home</h1>
        <p className="text-cream/30 text-sm mt-1">What's happening today</p>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          <h2 className="text-cream/30 text-[10px] uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <AlertTriangle size={11} /> Attention Required
          </h2>
          {alerts.map((a, i) => (
            <Link key={i} to={a.link} className="flex items-center gap-3 bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-2.5 hover:border-amber-500/30 transition-colors">
              <AlertTriangle size={13} className="text-amber-400 flex-shrink-0" />
              <span className="text-amber-300/80 text-xs">{a.msg}</span>
              <ArrowRight size={11} className="ml-auto text-amber-400/40" />
            </Link>
          ))}
        </div>
      )}

      {/* Today's Stats */}
      <div className="mb-8">
        <h2 className="text-cream/30 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Zap size={11} /> Today
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {todayCards.map((c) => (
            <StatCard key={c.label} {...c} value={loading ? '—' : c.value} />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-cream/30 text-[10px] uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/admin/compose" className="bg-navy border border-gold/[0.06] rounded-xl p-5 hover:border-gold/15 transition-all group" data-testid="quick-email-list">
            <Send size={18} className="text-gold/40 mb-3" />
            <h3 className="text-cream text-sm font-semibold mb-1">Email the list</h3>
            <p className="text-cream/25 text-xs">Send to subscribers</p>
          </Link>
          <Link to="/admin/payments" className="bg-navy border border-gold/[0.06] rounded-xl p-5 hover:border-gold/15 transition-all group" data-testid="quick-view-payments">
            <CreditCard size={18} className="text-gold/40 mb-3" />
            <h3 className="text-cream text-sm font-semibold mb-1">View Payments</h3>
            <p className="text-cream/25 text-xs">All Stripe transactions</p>
          </Link>
          <Link to="/admin/sessions" className="bg-navy border border-gold/[0.06] rounded-xl p-5 hover:border-gold/15 transition-all group" data-testid="quick-create-session">
            <Radio size={18} className="text-gold/40 mb-3" />
            <h3 className="text-cream text-sm font-semibold mb-1">Create session</h3>
            <p className="text-cream/25 text-xs">Set up a new CFQ room</p>
          </Link>
          <Link to="/admin/articles/new" className="bg-navy border border-gold/[0.06] rounded-xl p-5 hover:border-gold/15 transition-all group" data-testid="quick-new-article">
            <FileText size={18} className="text-gold/40 mb-3" />
            <h3 className="text-cream text-sm font-semibold mb-1">New Article</h3>
            <p className="text-cream/25 text-xs">Create and publish</p>
          </Link>
        </div>
      </div>

      {/* Content overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Articles', value: stats?.articles?.total || 0, sub: `${stats?.articles?.published || 0} published`, link: '/admin/articles' },
          { label: 'Events', value: stats?.events?.total || 0, sub: `${stats?.events?.upcoming || 0} upcoming`, link: '/admin/events' },
          { label: 'Social Drafts', value: (stats?.social?.draft || 0) + (stats?.social?.scheduled || 0), sub: `${stats?.social?.scheduled || 0} scheduled`, link: '/admin/social' },
          { label: 'Mailing List', value: cfqStats?.mailing_list_count || 0, sub: 'total subscribers', link: '/admin/mailing' },
          { label: 'Sessions', value: cfqStats?.by_session?.length || 0, sub: 'unique codes', link: '/admin/sessions' },
        ].map((c) => (
          <Link key={c.label} to={c.link} className="bg-navy border border-gold/[0.06] rounded-xl p-4 hover:border-gold/15 transition-all group">
            <p className="text-cream text-xl font-semibold">{loading ? '—' : c.value}</p>
            <p className="text-cream/30 text-xs mt-1">{c.label}</p>
            <p className="text-cream/15 text-[10px] mt-0.5">{c.sub}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
